import React, { useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Maximize2, BookOpen } from "lucide-react";

const Book = React.forwardRef(({ images, width, height }, ref) => (
  <HTMLFlipBook
    width={width} height={height} size="stretch"
    minWidth={260} maxWidth={620} minHeight={336} maxHeight={802}
    showCover={true} mobileScrollSupport={true} maxShadowOpacity={0.4}
    flippingTime={700} className="booklet-flip" ref={ref}
    onFlip={(e) => ref?.current?.__setPage && ref.current.__setPage(e.data)}
  >
    {images.map((src, i) => (
      <div key={i} className="bg-white overflow-hidden" data-testid={`booklet-page-${i + 1}`}>
        <img src={src} alt={`Page ${i + 1}`} className="w-full h-full object-contain select-none pointer-events-none" draggable={false} />
      </div>
    ))}
  </HTMLFlipBook>
));
Book.displayName = "Book";

function Viewer({ images, width, height }) {
  const ref = useRef(null);
  const [page, setPage] = useState(0);
  const total = images.length;

  const bind = (el) => {
    ref.current = el;
    if (el) el.__setPage = setPage;
  };
  const flip = (dir) => {
    const api = ref.current?.pageFlip?.();
    if (!api) return;
    dir < 0 ? api.flipPrev() : api.flipNext();
  };

  return (
    <div className="flex flex-col items-center" data-testid="booklet-viewer">
      <Book ref={bind} images={images} width={width} height={height} />
      <div className="flex items-center gap-4 mt-4">
        <Button variant="outline" size="sm" onClick={() => flip(-1)} disabled={page <= 0} data-testid="booklet-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-xs text-slate-500 tabular-nums" data-testid="booklet-indicator">Page {Math.min(page + 1, total)} of {total}</span>
        <Button variant="outline" size="sm" onClick={() => flip(1)} disabled={page >= total - 1} data-testid="booklet-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export function BookletFlip({ images = [] }) {
  const [full, setFull] = useState(false);
  if (!images.length) return null;
  return (
    <div data-testid="booklet-flip-wrap">
      <div className="rounded-2xl bg-slate-100 border border-slate-200 p-5 sm:p-8">
        <Viewer images={images} width={360} height={466} />
        <div className="flex justify-center mt-4">
          <Button variant="ghost" size="sm" onClick={() => setFull(true)} data-testid="booklet-fullscreen" className="text-[#006090]">
            <Maximize2 className="w-4 h-4 mr-1.5" /> Open full screen
          </Button>
        </div>
      </div>
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-5xl bg-slate-900 border-slate-800 p-6" data-testid="booklet-fullscreen-dialog">
          <DialogTitle className="text-white flex items-center gap-2 text-base"><BookOpen className="w-4 h-4" /> Retail Program Overview</DialogTitle>
          <div className="mt-2"><Viewer images={images} width={460} height={595} /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
